import { html, LitElement, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { styleMap } from "lit/directives/style-map.js";
import { classMap } from "lit/directives/class-map.js";

import { parseIntNullable } from "../../../modules/helpers";
import * as storage from "../storage";
import css from "./bible-video-player.css?inline";

type PlayerState = {
  ready: boolean;
  start: number;
};

@customElement("bible-video-player")
export class VideoPlayerElement extends LitElement {
  static styles = unsafeCSS(css);

  @property()
  book: string = null!;

  @property()
  cta: string = null!;

  @state()
  private playerState: PlayerState | null = null;

  private refs = {
    player: createRef<HTMLVideoElement>(),
  };

  @property({ attribute: "video-id" })
  videoId: string = null!;

  @property({ attribute: "video-mux-id" })
  videoMuxId: string = null!;

  @property({
    attribute: "video-start",
    converter: {
      fromAttribute: (value) => parseIntNullable(value) ?? 0,
      toAttribute: (value) => String(value),
    },
  })
  videoStart: number = 0;

  @property({ attribute: "video-thumbnail" })
  videoThumbnail: string = null!;

  @property({ attribute: "video-title" })
  videoTitle: string = null!;

  @state()
  private isCollapsed = storage.get("desktop.video.collapsed");

  private handleMinimizeVideo() {
    this.isCollapsed = !this.isCollapsed;
    storage.set("desktop.video.collapsed", this.isCollapsed);
  }

  render() {
    return html`
      <div
        class=${classMap({
          video_player: true,
          "is--active": !this.isCollapsed,
        })}
      >
        <button class="video_player_action_button BibleBookExplore__minimize">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.125 12.5C20.125 13.2031 19.5391 13.75 18.875 13.75H5.125C4.42188 13.75 3.875 13.2031 3.875 12.5C3.875 11.8359 4.42188 11.25 5.125 11.25H18.875C19.5391 11.25 20.125 11.8359 20.125 12.5Z"
              fill="white"
            />
          </svg>
        </button>
        <button
          @click=${this.handleMinimizeVideo}
          class="video_player_action_button BibleBookExplore__toggle"
          aria-expanded=${!this.isCollapsed}
          aria-controls="description,button1,button2"
        >
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.28125 8.71875L1.28125 2.71875C0.875 2.34375 0.875 1.6875 1.28125 1.3125C1.65625 0.90625 2.3125 0.90625 2.6875 1.3125L8 6.59375L13.2812 1.3125C13.6562 0.90625 14.3125 0.90625 14.6875 1.3125C15.0938 1.6875 15.0938 2.34375 14.6875 2.71875L8.6875 8.71875C8.3125 9.125 7.65625 9.125 7.28125 8.71875Z"
              fill="white"
            />
          </svg>
        </button>
        ${this.playerState
          ? html`
              <mux-player
                @loadeddata=${() => {
                  const $player = this.refs.player.value!;
                  const state = this.playerState!;
                  $player.currentTime = state.start;
                  this.playerState = { ...state, ready: true };
                }}
                ${ref(this.refs.player)}
                default-hidden-captions
                metadata-env-key="1h9kgiasjnic5jpvi4bk1snit"
                metadata-player-name="Bible Reader"
                metadata-video-id=${this.videoId}
                metadata-video-stream-type="on-demand"
                metadata-video-title=${this.videoTitle}
                playback-id=${this.videoMuxId}
                playsinline
                stream-type="on-demand"
              ></mux-player>
            `
          : html`
              <div
                class="overlay"
                style=${styleMap({
                  "--image-url": `url(${this.videoThumbnail})`,
                })}
              >
                <div class="video_content">
                  <h3>Explore ${this.book} Visually</h3>

                  <p id="description">
                    Watch our Biblical Overview video and learn the context and
                    story of the book of ${this.book}.
                  </p>
                  <button
                    id="button1"
                    @click=${() => {
                      this.#showPlayer(this.videoStart);
                    }}
                  >
                    ${this.cta}
                  </button>
                  ${this.videoStart > 0
                    ? html`
                        <button
                          id="button2"
                          @click=${() => {
                            this.#showPlayer();
                          }}
                          class="secondary"
                        >
                          Watch from Beginning
                        </button>
                      `
                    : null}
                </div>
              </div>
            `}
      </div>
    `;
  }

  async #showPlayer(start: number = 0) {
    // @ts-ignore
    import("https://cdn.jsdelivr.net/npm/@mux/mux-player");
    this.playerState = {
      ready: false,
      start,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-video-player": VideoPlayerElement;
  }
}
